import React, { useEffect, useState } from 'react'
import { Button, Grid2, Typography } from '@mui/material'
import { GRAY_COLORS } from 'asterX-UI'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { AutoCompleteField } from 'asterX-UI/Inputs'
import MaskedTextInput from 'asterX-UI/Inputs/MackedTextInput'
import ConfirmationCodeInput from 'asterX-UI/Inputs/ConfirmationCodeInput'
import { Link, useNavigate } from 'react-router-dom'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { debounce } from 'lodash'
import { useResErrors } from 'lib/hooks'
import { useSnackbar } from 'notistack'
import { getFullName, getIinInputText } from 'lib/utils'
import { useAuthStore } from 'context/AuthStore'

const CreateLoanApplicationModal = ({ open, onClose, clientGuarantee = null }) => {
  const { authStoreFunc } = useAuthStore()
  const navigate = useNavigate()
  const { standardCheck } = useResErrors()
  const { enqueueSnackbar } = useSnackbar()
  const [client, setClient] = useState([])
  const [loading, setLoading] = useState(false)
  const [iinError, setIinError] = useState(false)
  const [newClient, setNewClient] = useState(null)
  const [smsCodeError, setSmsCodeError] = useState(false)

  const [modalState, setModalState] = useState({
    isCreatingClient: false,
    isCodeSent: false,
    phoneNumber: '',
    confirmationCode: ['', '', '', ''],
    clientData: {
      iinInput: '',
      selectedClient: null
    },
    isResending: false
  })

  const handleInputChange = (event, newInputValue) => {
    setModalState(prevState => ({
      ...prevState,
      clientData: {
        ...prevState.clientData,
        iinInput: newInputValue
      }
    }))

    const clientIdExists = modalState.clientData?.selectedClient?.id

    if (!clientIdExists) {
      if (/^\d{0,12}$/.test(newInputValue)) {
        setIinError(false)
      } else {
        setIinError(true)
      }
    }
  }

  const handleSelectClient = event => {
    const { value } = event.target
    setModalState(prevState => ({
      ...prevState,
      clientData: {
        iinInput: value?.iin || '',
        selectedClient: value || null
      },
      clientId: value?.id || '',
      phoneNumber: value?.mobilePhone1 || '',
      isCreatingClient: true
    }))
    setIinError(false)
  }

  const handlePhoneNumberChange = event => {
    setModalState(prevState => ({
      ...prevState,
      phoneNumber: event.target.value
    }))
  }

  const handleSendCode = async () => {
    if (!modalState.clientData.selectedClient.iin) {
      enqueueSnackbar('ИИН не найден', { variant: 'error' })
      return
    }
    try {
      const data = {
        iin: modalState.clientData.selectedClient.iin,
        phone: modalState.phoneNumber,
        clientId: modalState.clientId
      }
      setLoading(true)
      const response = await CreditFlow.createByOtp(data)
      standardCheck(response, () => {
        if (response?.data) {
          setNewClient(response?.data)
          setModalState(prevState => ({
            ...prevState,
            isCodeSent: true,
            isResending: false
          }))
        }
      })
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCheckOtp = async () => {
    if (!modalState.confirmationCode.join('')) {
      enqueueSnackbar('Ошибка', { variant: 'error' })
      return
    }
    console.log("newClient:" + newClient?.id)
    try {
      const data = {
        otpCode: modalState.confirmationCode.join(''),
        clientId: modalState.clientId,
        iin: modalState.clientData.iinInput,
        phone: modalState.phoneNumber
      }
      authStoreFunc.setLoading(true)
      const response = await CreditFlow.checkOtp(data)
      console.error("zzzzz:" + response?.data)
      if (response?.data) {
        setSmsCodeError(false)
        authStoreFunc.setLoading(false)
        navigate(`/new-application/${response?.data?.id}`)
      } else {
        setSmsCodeError(true)
      }
    } catch (error) {
      console.error(error)
    } finally {
      authStoreFunc.setLoading(false)
    }
  }

  const handleResendCode = () => {
    if (!modalState.isResending) {
      handleSendCode()
      setModalState(prevState => ({
        ...prevState,
        isResending: true
      }))
      setTimeout(() => {
        setModalState(prevState => ({
          ...prevState,
          isResending: false
        }))
      }, 5000)
    }
  }

  const resetModalState = () => {
    setModalState({
      isCreatingClient: false,
      isCodeSent: false,
      phoneNumber: '',
      confirmationCode: ['', '', '', ''],
      clientData: {
        iinInput: '',
        selectedClient: null
      },
      isResending: false
    })
    onClose()
  }

  const handleNewApplication = async () => {
    if (!client?.[0]?.id) {
      enqueueSnackbar('Ошибка', { variant: 'error' })
      return
    }

    const res = await CreditFlow.createByExistingClient({
      clientId: client?.[0]?.id
    })

    if (res?.data) {
      navigate(`/new-application/${res?.data?.id}`)
    }
  }

  const searchClientByIIN = debounce(async iin => {
    try {
      setLoading(true)
      const response = await CreditFlow.searchClientByIIN(iin)
      standardCheck(response, async () => {
        if (response?.data?.id) {
          setClient([response.data])
        } else if (iinError) {
          setClient([])
        } else {
          setClient([{ firstName: '+ Создать нового клиента', iin: modalState.clientData.iinInput }])
        }
      })
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, 500)

  useEffect(() => {
    const { iinInput } = modalState.clientData

    if (iinInput.length === 12) {
      searchClientByIIN(iinInput)
    } else if (iinInput.length < 12) {
      setClient([])
    }

    return () => searchClientByIIN.cancel()
  }, [modalState.clientData.iinInput])

  useEffect(() => {
    const { confirmationCode } = modalState

    if (confirmationCode.join('') === '') {
      setSmsCodeError(false)
    }
  }, [modalState.confirmationCode])

  useEffect(() => {
    if (open && clientGuarantee?.id) {
      setClient([clientGuarantee])
      setModalState(prevState => ({
        ...prevState,
        clientData: {
          ...prevState.clientData,
          selectedClient: clientGuarantee,
          iinInput: clientGuarantee.iin
        }
      }))
    }
  }, [open, clientGuarantee?.id])

  return (
    <ActionModal
      open={open}
      onClose={resetModalState}
      title={'Создать заявку'}
      subtitle={
        modalState.isCreatingClient
          ? 'Получите согласие клиента на обработку данных'
          : 'Выберите клиента или создайте нового'
      }
    >
      <Grid2
        container
        sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '12px', marginTop: '2px' }}
      >
        {!modalState.isCreatingClient ? (
          <>
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="small">
                {modalState.clientData.selectedClient ? 'Клиент' : getIinInputText(modalState.clientData.iinInput)}
              </Typography>
              <AutoCompleteField
                name="clientIIN"
                loading={loading}
                value={modalState.clientData.selectedClient}
                placeholder="Введите ИИН"
                options={client}
                getOptionLabel={option => getFullName(option?.firstName, option?.lastName, option?.patronymic)}
                onChange={handleSelectClient}
                onInputChange={handleInputChange}
                fullWidth
                filterOptions={(options, { inputValue }) => options.filter(option => option.iin.startsWith(inputValue))}
                error={iinError}
                onInput={event => {
                  event.target.value = event.target.value.replace(/\D/g, '').slice(0, 12)
                }}
              />
            </Grid2>
            <Button
              variant="contained"
              disabled={!modalState.clientData.iinInput && !modalState.clientData.selectedClient}
              fullWidth
              onClick={handleNewApplication}
            >
              Выбрать
            </Button>
          </>
        ) : !modalState.isCodeSent ? (
          <>
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="small">Номер клиента</Typography>
              <MaskedTextInput
                name="phoneNumber"
                fullWidth
                mask="+7-(999)-999-99-99"
                placeholder="Номер клиента"
                value={modalState.phoneNumber}
                onChange={handlePhoneNumberChange}
              />
            </Grid2>
            <Button
              variant="contained"
              disabled={!modalState.phoneNumber || modalState.isResending}
              fullWidth
              onClick={handleSendCode}
            >
              {modalState.isResending ? 'Отправляем...' : 'Отправить'}
            </Button>
          </>
        ) : (
          <>
            <Grid2 size={{ xs: 12 }}>
              <ConfirmationCodeInput
                smsCodeError={smsCodeError}
                code={modalState.confirmationCode}
                setCode={newCode => setModalState(prevState => ({ ...prevState, confirmationCode: newCode }))}
              />
              <Typography variant="body2" style={{ color: GRAY_COLORS[600], marginTop: 5 }}>
                Не получили код?{' '}
                <Link onClick={handleResendCode} disabled={modalState.isResending} style={{ color: GRAY_COLORS[600] }}>
                  {modalState.isResending ? 'Отправляем...' : 'Переотправить'}
                </Link>
              </Typography>
            </Grid2>
            <Button
              variant="contained"
              disabled={modalState.confirmationCode.join('').length < 4 || modalState.isResending}
              fullWidth
              onClick={handleCheckOtp}
            >
              Подтвердить
            </Button>
          </>
        )}
      </Grid2>
    </ActionModal>
  )
}

export default CreateLoanApplicationModal
